import React, { useCallback, useMemo, useState } from "react";

import ChevronDown from "@uui/ChevronDown";
import SwitchVertical from "@uui/SwitchVertical01";
import { filter, map, pick, partition, times, isEmpty } from "lodash/fp";

import { ButtonGroup } from "@mui/material";
import styled from "@mui/material/styles/styled";

import ReportSubsection from "@app/analytics/components/reports/ReportSubsection";
import ParticipantCard, {
  AbsentParticipantsCard,
} from "@app/analytics/MeetingReport/components/ParticipantCard";
import SortByMenu from "@app/analytics/MeetingReport/components/SortByMenu";
import useMeetingReport from "@app/analytics/MeetingReport/hooks/useMeetingReport";
import useMeetingReportMetricsQuery from "@app/analytics/MeetingReport/hooks/useMeetingReportMetricsQuery";
import useSessionMetadata from "@app/analytics/MeetingReport/hooks/useSessionMetadata";
import DeepDiveChart from "@app/analytics/MeetingReport/MeetingReportSidebarContent/DeepDiveChart";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import { LoadingSkeleton } from "@app/analytics/MeetingReport/styles";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import {
  Participant,
  ReelClip,
  SpeakerEvent,
  VideoReel,
} from "@app/analytics/MeetingReport/types";
import useVideoControls from "@app/analytics/MeetingReport/VideoPlayer/useVideoControls";
import { ParsedMetric, QueryMetric } from "@app/analytics/meetops/metrics";
import { TalkTurnData } from "@app/analytics/types";
import useDataTable from "@app/common/hooks/useDataTable";
import {
  parseLocalDateTimeFromString,
  parseUTCDateTime,
} from "@app/utils/utils";

import { DropdownItem } from "@rui/Dropdown/types";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import {
  EmptyStateContainer,
  EmptyStateSubtext,
  EmptyStateTitle,
} from "@rui/EmptyStateContainer";
import Stack from "@rui/Stack";

import {
  DeepDiveMenuButton,
  DeepDiveSectionHeader,
  DeepDiveSectionHeading,
} from "./styles";

const PARTICIPANTS_SORT_OPTIONS = [
  {
    id: "talkTime",
    value: "talkTime",
    label: "Sort by talk time",
    sortBy: (participant: Participant) =>
      -participant.talkTimeData.talkTimePercent,
  },
  {
    id: "alphabetical",
    value: "alphabetical",
    label: "Sort alphabetically",
    sortBy: (participant: Participant) => participant.name,
  },
];

const DROPDOWN_OPTIONS = map(
  pick(["value", "label"]),
  PARTICIPANTS_SORT_OPTIONS
) as DropdownItem[];

interface ParticipantTableRow {
  participantBias: ParsedMetric;
  participantCharisma: ParsedMetric;
  participantEngagement: ParsedMetric;
  participantLateBy: ParsedMetric;
  participantMeetingScore: ParsedMetric;
  participantSentiment: ParsedMetric;
  participantObservedJoinTime: ParsedMetric;
  participantTalkTime: ParsedMetric;
  participantTimeInMeeting: ParsedMetric;
  talkTimePercent: ParsedMetric;
  onTimeRate: ParsedMetric;
  participantName?: string;
}

const PARTICIPANT_METRICS: QueryMetric[] = [
  "participantMeetingScore",
  "participantSentiment",
  "participantEngagement",
  "participantAttendance",
  "participantTimeInMeeting",
  "participantTalkTime",
  "participantLateBy",
  "participantObservedJoinTime",
  "meetingDurationActual",
  "talkTimePercent",
  "participantCharisma",
  "participantBias",
  "microphoneOffPercent",
  "cameraOffPercent",
];

const makeVideoReel = (
  participant: Participant,
  meetingStartTime: number,
  videoStartTimeOffset: number
): VideoReel => {
  const clips: ReelClip[] = map((talkTurn) => {
    const turnStartTime =
      typeof talkTurn.startTime === "number"
        ? talkTurn.startTime
        : parseUTCDateTime(talkTurn.startTime).toMillis();

    const turnEndTime =
      typeof talkTurn.endTime === "number"
        ? talkTurn.endTime
        : parseUTCDateTime(talkTurn.endTime).toMillis();

    return {
      startTimestamp:
        Math.round((turnStartTime - meetingStartTime) / 1000) +
        videoStartTimeOffset,
      endTimestamp:
        Math.round((turnEndTime - meetingStartTime) / 1000) +
        videoStartTimeOffset,
    };
  }, participant.talkTimeData.talkTurns);

  return {
    clips: clips,
    playing: true,
    name: getReelName(participant),
  };
};

const getReelName = (participant: Participant) =>
  `participant:${participant.name}`;

const isParticipantReel = (reel: VideoReel | null, participant: Participant) =>
  Boolean(reel && reel.name === getReelName(participant));

const getParticipantFromTableRow = (
  row: ParticipantTableRow,
  talkTurns: TalkTurnData[]
): Participant => {
  const present = Boolean(row.participantTimeInMeeting.value);
  const wasLate =
    Boolean(row.participantTimeInMeeting.value) &&
    Boolean(row.participantLateBy.rawValue);

  const joinTime = row.participantObservedJoinTime.rawValue
    ? parseLocalDateTimeFromString(
        row.participantObservedJoinTime.rawValue as unknown as string
      )
    : undefined;

  return {
    name: row.participantName ?? "Unidentified",
    meetingScore: row.participantMeetingScore.value,
    sentiment: row.participantSentiment.value,
    engagement: row.participantEngagement.value,
    bias: row.participantBias.value,
    charisma: row.participantCharisma.value,
    talkTimeData: {
      talkTimePercent: row.talkTimePercent.value ?? 0,
      talkTurns,
    },
    firstJoin: joinTime,
    present,
    onTime: !wasLate,
  };
};

const getParticipantsFromResults = (
  rows: ParticipantTableRow[],
  speakerEvents: SpeakerEvent[]
): Participant[] => {
  return map((row) => {
    const rowSpeakerEvents = filter(
      (event) => row.participantName === event.speaker,
      speakerEvents
    );

    const rowTalkTurns = map(
      (event) => ({
        startTime: event.rawStartTime.toMillis(),
        endTime: event.rawEndTime.toMillis(),
      }),
      rowSpeakerEvents
    );

    return getParticipantFromTableRow(row, rowTalkTurns);
  }, rows);
};

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  "& .MuiButtonGroup-grouped:first-of-type": {
    "&:hover": {
      borderColor: theme.palette.shades.primary[500],
    },
    zIndex: 1,
  },
}));

const DeepDiveParticipation = () => {
  const { meetingReport, speakerEvents } = useMeetingReport();
  const {
    data: {
      metricsLevel,
      isUploadedSession,
      recordingEnabled,
      startTime: sessionMetadataStartTime,
      endTime: sessionMetadataEndTime,
    },
  } = useSessionMetadata();

  const [selectedSort, setSelectedSort] = useState<DropdownItem>(
    PARTICIPANTS_SORT_OPTIONS[0]
  );

  const {
    videoReel,
    setVideoReel,
    setVideoReelClipIndex,
    paused,
    playbackEnabled,
    videoStartTimeOffset,
  } = useVideoStore((state) => ({
    videoReel: state.videoReel,
    setVideoReel: state.setVideoReel,
    setVideoReelClipIndex: state.setVideoReelClipIndex,
    paused: state.paused,
    playbackEnabled: state.playbackEnabled,
    videoStartTimeOffset: state.videoStartTimeOffset,
  }));

  const { play, pause } = useVideoControls();

  const handlePlayToggle = useCallback(
    (shouldPlay: boolean, participant: Participant) => {
      if (
        shouldPlay &&
        !isParticipantReel(videoReel, participant) &&
        meetingReport?.rawStartTime
      ) {
        Events.playParticipantReel();
        setVideoReel(
          makeVideoReel(
            participant,
            meetingReport.rawStartTime,
            videoStartTimeOffset
          )
        );
        play();
      } else if (shouldPlay) {
        // If the reel is the same as the one in the store
        // just pick up where we left off.
        play();
      } else {
        pause();
      }
    },
    [videoReel?.name, meetingReport?.rawStartTime]
  );

  const handleTurnClick = useCallback(
    (turnIndex: number) => {
      setVideoReelClipIndex(turnIndex);
    },
    [videoReel?.name]
  );

  const participantResults = useMeetingReportMetricsQuery(
    {
      dimensions: ["participantName"],
      metrics: PARTICIPANT_METRICS,
    },
    {
      queryKey: ["participants"],
    }
  );

  const participantResultsData: ParticipantTableRow[] =
    participantResults?.data?.results[0].data ?? [];

  const participants = useMemo(
    () => getParticipantsFromResults(participantResultsData, speakerEvents),
    [participantResultsData, speakerEvents]
  );

  const [presentParticipants, absentParticipants] = partition(
    (p) => p.present,
    participants
  );

  const { handleSort, sortedData, setOrder, order } = useDataTable(
    presentParticipants,
    PARTICIPANTS_SORT_OPTIONS,
    {
      defaultOrder: "asc",
    }
  );

  const handleChangeSort = (e: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedSort(getSelectedFromOptions(PARTICIPANTS_SORT_OPTIONS, id)!);
    handleSort(e, id);
  };

  const handleSwapOrder = () => {
    setOrder(order === "asc" ? "desc" : "asc");
  };

  const showSortByMenu = sortedData.length > 1;

  // uploaded sessions need to *always* use the session metadata start/end time,
  // since those dates are responsive to the user input
  const meetingStartTime = isUploadedSession
    ? sessionMetadataStartTime?.toMillis()
    : sessionMetadataStartTime?.toMillis() ?? meetingReport?.rawStartTime;
  const meetingEndTime = isUploadedSession
    ? sessionMetadataEndTime?.toMillis()
    : sessionMetadataEndTime?.toMillis() ?? meetingReport?.rawEndTime;

  return (
    <ReportSubsection id="participation">
      <DeepDiveSectionHeader>
        <DeepDiveSectionHeading>Participation</DeepDiveSectionHeading>
        <StyledButtonGroup variant="outlined">
          <DeepDiveMenuButton onClick={handleSwapOrder}>
            <SwitchVertical />
          </DeepDiveMenuButton>
          {showSortByMenu && (
            <SortByMenu
              options={DROPDOWN_OPTIONS}
              selectedSort={selectedSort}
              onChange={handleChangeSort}
            >
              <DeepDiveMenuButton endIcon={<ChevronDown />} variant="outlined">
                {selectedSort.label}
              </DeepDiveMenuButton>
            </SortByMenu>
          )}
        </StyledButtonGroup>
      </DeepDiveSectionHeader>
      <Stack spacing={1}>
        {participantResults.isLoading ? (
          times(
            (i) => <LoadingSkeleton key={i} height={46} />,
            meetingReport?.participants?.length ?? 1
          )
        ) : isEmpty(sortedData) ? (
          <EmptyStateContainer sx={{ py: 1.5 }} spacing={1.5}>
            <EmptyStateTitle>Nothing to see here</EmptyStateTitle>
            <EmptyStateSubtext>
              No participants detected in this meeting
            </EmptyStateSubtext>
          </EmptyStateContainer>
        ) : (
          sortedData.map((participant) => (
            <ParticipantCard
              key={`${participant.name}-${participant.talkTimeData?.talkTimePercent}-${videoReel?.name}`}
              participant={participant}
              meetingStartTime={meetingStartTime}
              meetingEndTime={meetingEndTime}
              onPlayToggle={handlePlayToggle}
              onTurnClick={handleTurnClick}
              isReelSelected={isParticipantReel(videoReel, participant)}
              isPlaying={isParticipantReel(videoReel, participant) && !paused}
              playable={playbackEnabled && recordingEnabled}
              expandable={metricsLevel !== "objective_metrics"}
            />
          ))
        )}
        {absentParticipants.length > 0 && (
          <AbsentParticipantsCard participants={absentParticipants} />
        )}
      </Stack>
      <Stack showBelow="mobile" mt={1}>
        <DeepDiveChart />
      </Stack>
    </ReportSubsection>
  );
};

export default DeepDiveParticipation;
